import { computeAspectRatio, fileStatus, resolveImageSlotRatio } from './media'
import { insertMediaItems } from './mediaPayload'

const inferImageType = (filename = '') => {
  const lowerName = filename.toLowerCase()

  if (lowerName.endsWith('.png')) {
    return 'image/png'
  }

  if (lowerName.endsWith('.jpg') || lowerName.endsWith('.jpeg')) {
    return 'image/jpeg'
  }

  return 'image/jpeg'
}

export const collectSelectedGeneratedImages = (variants, selectedIds) => {
  const selectedSet = new Set(selectedIds)

  return variants.flatMap((variant) =>
    variant.groups.flatMap((group) =>
      group.images
        .filter((image) => selectedSet.has(image.id))
        .map((image) => ({
          ...image,
          group_id: image.group_id ?? group.group_id,
          sub_group_id: image.sub_group_id ?? group.sub_group_id ?? group.id,
          headline: image.headline ?? group.headline ?? null,
        })),
    ),
  )
}

const resolveAppliedSubGroupId = (image, media) => {
  if (image.sub_group_id) {
    return image.sub_group_id
  }

  // Ratio variants without sub_group_id: inherit from linked original.
  if (image.headline == null && image.group_id) {
    const original = media.find((item) => String(item.id) === String(image.group_id))
    return original?.sub_group_id ?? null
  }

  return null
}

const mapGeneratedImageToMediaItem = (image) => {
  const calculatedRatio = image.width && image.height
    ? computeAspectRatio(image.width, image.height)
    : null

  return {
    id: image.id,
    url: image.url,
    filename: image.filename,
    width: image.width,
    height: image.height,
    size: image.size,
    type: image.type ?? inferImageType(image.filename),
    ratio: calculatedRatio ?? image.ratio ?? null,
    // group_id is a transient insert-position hint — insertMediaItems strips it before storing.
    group_id: image.group_id,
    sub_group_id: image.sub_group_id,
    headline: image.headline ?? null,
    is_generated: true,
    status: fileStatus.success,
  }
}

const getOccupiedSlotsForSubGroup = (media, subGroupId) => {
  const occupied = new Set()

  media.forEach((item) => {
    if (String(item.sub_group_id) !== String(subGroupId)) {
      return
    }

    const slot = resolveImageSlotRatio(item)
    if (slot) {
      occupied.add(slot)
    }
  })

  return occupied
}

export const applyGeneratedVariantsToMedia = (media, variants, selectedIds) => {
  const selectedImages = collectSelectedGeneratedImages(variants, selectedIds)

  if (!selectedImages.length) {
    return media
  }

  const selectedImagesBySubGroup = selectedImages.reduce((acc, image) => {
    const subGroupId = resolveAppliedSubGroupId(image, media)
    if (!subGroupId) {
      return acc
    }

    const normalizedImage = {
      ...image,
      sub_group_id: subGroupId,
    }

    if (!acc.has(subGroupId)) {
      acc.set(subGroupId, [])
    }

    acc.get(subGroupId).push(normalizedImage)
    return acc
  }, new Map())

  const batches = [...selectedImagesBySubGroup.entries()].map(([subGroupId, groupImages]) => ({
    subGroupId,
    groupImages,
    hasHeadline: groupImages.some((image) => image.headline != null),
  }))

  const ratioBatches = batches.filter((batch) => !batch.hasHeadline)
  const headlineBatches = batches.filter((batch) => batch.hasHeadline)
  const orderedBatches = [...ratioBatches, ...headlineBatches]

  let nextMedia = [...media]

  orderedBatches.forEach(({ subGroupId, groupImages }) => {
    const occupiedSlots = getOccupiedSlotsForSubGroup(nextMedia, subGroupId)
    const itemsToInsert = []

    groupImages.forEach((image) => {
      const mediaItem = mapGeneratedImageToMediaItem(image)
      const slot = resolveImageSlotRatio(mediaItem)

      // Skip if this placement slot is already filled in the subgroup
      // (protects from wrong BE ratio labels / duplicate applies).
      if (slot && occupiedSlots.has(slot)) {
        return
      }

      if (slot) {
        occupiedSlots.add(slot)
      }

      itemsToInsert.push(mediaItem)
    })

    if (itemsToInsert.length) {
      nextMedia = insertMediaItems(nextMedia, itemsToInsert)
    }
  })

  return nextMedia
}
